import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { MapPin, Bed, Bath, Square, Clock, Check, X, Heart, RefreshCw, Award, TrendingUp, Info, Hammer, Home as HomeIcon, AlertTriangle, Target, Wrench } from "lucide-react";
import ShareDeal from "./ShareDeal";
import { PropertyImage } from "./PropertyImage";
import { DataSourcesFooter, ConfidenceChip } from "./analysis/DataSourceProvenance";
import { formatCurrency, formatPercent, PropertyTypeIcon, buildPropertyAltText } from "../lib/helpers";
import { trackPropertyClick } from "../lib/tracking";
import { useState } from "react";

const getRelativeTime = (isoString) => {
  if (!isoString) return null;
  try {
    const diff = (Date.now() - new Date(isoString).getTime()) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 172800) return "Yesterday";
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return null; }
};

const STATUS_CONFIG = {
  for_sale: { label: "Active", color: "bg-emerald-600/90", dot: "bg-emerald-400" },
  pending: { label: "Pending", color: "bg-amber-600/90", dot: "bg-amber-400" },
  recently_sold: { label: "Sold", color: "bg-purple-600/90", dot: "bg-purple-400" },
  foreclosed: { label: "Foreclosure", color: "bg-red-600/90", dot: "bg-red-400" },
  off_market: { label: "Off Market", color: "bg-gray-600/90", dot: "bg-gray-400" },
  seized: { label: "Seized", color: "bg-orange-600/90", dot: "bg-orange-400" },
  for_rent: { label: "For Rent", color: "bg-blue-600/90", dot: "bg-blue-400" },
};

const SCORE_REASONS = {
  90: "Exceptional cash flow, strong cap rate, passes all rules",
  75: "Great numbers — solid cash flow and cap rate",
  60: "Above average — decent returns with room to negotiate",
  50: "Fair deal — meets some investment benchmarks",
  0: "Below average — thin margins or negative cash flow",
};

const FLIP_SCORE_REASONS = {
  70: "Strong flip — high-confidence ARV, solid margins, profitable even in conservative case",
  50: "Viable flip — decent spread but verify ARV and rehab costs independently",
  30: "Risky flip — thin margins or low data confidence, needs due diligence",
  0: "Not recommended — negative profit, low confidence data, or margins too thin",
};

function getScoreReason(score, isFlip = false) {
  if (isFlip) {
    if (score >= 70) return FLIP_SCORE_REASONS[70];
    if (score >= 50) return FLIP_SCORE_REASONS[50];
    if (score >= 30) return FLIP_SCORE_REASONS[30];
    return FLIP_SCORE_REASONS[0];
  }
  if (score >= 90) return SCORE_REASONS[90];
  if (score >= 75) return SCORE_REASONS[75];
  if (score >= 60) return SCORE_REASONS[60];
  if (score >= 50) return SCORE_REASONS[50];
  return SCORE_REASONS[0];
}

const PropertyCard = ({ property, onClick, analysis, isSaved, onSaveToggle, showFlipData, showAirbnbData, strategyMode, subscription, onRefreshed }) => {
  const [showWhy, setShowWhy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const isFlipMode = strategyMode === 'flip';
  const isIncomeProducing = analysis?.is_income_producing;
  const onePercentRule = analysis?.one_percent_rule;
  const fiftyPercentRule = analysis?.fifty_percent_rule;
  const hasHighMatch = property.preference_score >= 15;
  const subType = analysis?.property_sub_type;
  // Context-aware deal score
  const rentalDealScore = analysis?.deal_score;
  const flipDealScore = analysis?.fix_flip?.deal_score;
  const dealScore = (isFlipMode || showFlipData) && flipDealScore != null ? flipDealScore : rentalDealScore;
  const scoreLabel = (isFlipMode || showFlipData) && flipDealScore != null ? 'Flip Score' : 'Deal Score';

  // Strategy recommendation
  const ff = analysis?.fix_flip;
  const rentalCF = analysis?.cash_flow_monthly ?? 0;
  const flipProfit = ff?.net_profit ?? 0;
  const bestStrategy = !analysis ? null : (flipProfit > 20000 && flipProfit > rentalCF * 24) ? 'flip' : rentalCF > 200 ? 'rental' : flipProfit > 0 ? 'flip' : rentalCF > 0 ? 'rental' : null;

  // Over ARV check
  const overARV = ff && ff.arv > 0 && ff.purchase_price >= ff.arv;

  const handleSaveClick = (e) => {
    e.stopPropagation();
    onSaveToggle(property.id);
  };

  // Feb 2026 — force-refresh from Zillow (Elite-only). Bypasses cache
  // and re-fetches the property from Zillow's live API so a user who
  // spots stale/wrong data can self-serve a fix instead of waiting on
  // the 6h background self-heal.
  const isElite = subscription?.tier === "elite_monthly" || subscription?.tier === "elite_yearly";
  const handleRefresh = async (e) => {
    e.stopPropagation();
    if (refreshing) return;
    setRefreshing(true);
    try {
      const axios = (await import("axios")).default;
      const { API } = await import("../lib/helpers");
      const { toast } = await import("sonner");
      const res = await axios.post(`${API}/properties/${property.id}/refresh`);
      if (res.data?.refreshed) {
        toast.success("Refreshed from Zillow");
        if (onRefreshed) onRefreshed(res.data.property);
      } else {
        toast.info(res.data?.reason || "Zillow had no data for this address");
      }
    } catch (err) {
      const { toast } = await import("sonner");
      if (err.response?.status === 403) {
        toast.error("Refresh is an Elite feature");
      } else {
        toast.error("Failed to refresh from Zillow");
      }
    } finally {
      setRefreshing(false);
    }
  };

  // Source badge color — visual signal so users know at a glance
  // which provider fed this row.
  const sourceKey = (property.data_source || property._source || property.listing_source || "zillow").toLowerCase();
  const sourceBadgeClass =
    sourceKey === "zillow" || sourceKey.includes("zillow") ? "bg-blue-600/90" :
    sourceKey === "rentcast" || sourceKey.includes("rentcast") ? "bg-emerald-600/90" :
    sourceKey === "realie" || sourceKey.includes("realie") ? "bg-amber-600/90" :
    property.is_seized ? "bg-orange-600/90" : "bg-slate-700/90";

  const handleClick = () => {
    if (analysis) {
      trackPropertyClick(property.id, dealScore, property.price);
    }
    onClick?.();
  };

  const handleWhyClick = (e) => {
    e.stopPropagation();
    setShowWhy(prev => !prev);
  };

  return (
    <Card
      className={`bg-card border rounded-xl overflow-hidden cursor-pointer card-hover animate-fade-in relative ${hasHighMatch ? 'border-amber-500/50 ring-1 ring-amber-500/20' : 'border-border'}`}
      onClick={handleClick}
      data-testid={`property-card-${property.id}`}
    >
      {dealScore != null && (
        <div className={`h-1 ${dealScore >= 75 ? 'bg-emerald-500' : dealScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${dealScore}%` }} />
      )}

      <div className="relative">
        <PropertyImage
          property={property}
          alt={buildPropertyAltText(property, analysis)}
          imgClassName="w-full h-44 object-cover"
        />
        <div className="absolute top-2.5 left-2.5 flex gap-1.5 flex-wrap">
          <Badge variant="secondary" className="bg-black/70 text-white border-0 text-[10px] py-0.5">
            <PropertyTypeIcon type={property.property_type} />
            <span className="ml-1 capitalize">{property.property_type.replace('_', ' ')}</span>
          </Badge>
          {subType && <Badge className="bg-blue-500/80 text-white border-0 text-[10px] py-0.5" data-testid="multi-subtype-badge">{subType}</Badge>}
          {hasHighMatch && <Badge className="bg-amber-500/90 text-white border-0 text-[10px] py-0.5" data-testid="top-match-badge">Top Match</Badge>}
          {(property.days_on_market != null && property.days_on_market <= 7 && property.days_on_market > 0) && (
            <Badge className="bg-emerald-500/90 text-white border-0 text-[10px] py-0.5" data-testid="new-listing-badge">
              NEW · {property.days_on_market}d
            </Badge>
          )}
          {(() => {
            const status = property.listing_status || "for_sale";
            const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.for_sale;
            return (
              <Badge className={`${cfg.color} text-white border-0 text-[10px] py-0.5 flex items-center gap-1`} data-testid="listing-status-badge">
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} animate-pulse`} />
                {cfg.label}
              </Badge>
            );
          })()}
        </div>
        <div className="absolute top-2.5 right-2.5 flex gap-1.5">
          <ShareDeal property={property} variant="icon" />
          <Button
            variant="ghost" size="icon"
            className={`h-7 w-7 rounded-full ${isSaved ? 'bg-red-500/80 hover:bg-red-500' : 'bg-black/50 hover:bg-black/70'}`}
            onClick={handleSaveClick}
            data-testid={`save-btn-${property.id}`}
          >
            <Heart className={`w-3.5 h-3.5 ${isSaved ? 'fill-white text-white' : 'text-white'}`} />
          </Button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-8 pb-2.5 px-3 flex items-end justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-xl font-bold text-white" data-testid="property-price">{formatCurrency(property.price)}</span>
            {property.price_change && property.price_change < 0 && (
              <Badge className="bg-emerald-600/90 text-white border-0 text-[9px] py-0" data-testid="price-drop-badge">
                {formatCurrency(property.price_change)}
              </Badge>
            )}
          </div>
          <Badge className={`border-0 text-white text-[9px] py-0.5 ${sourceBadgeClass}`} data-testid="source-badge">
            {property.is_seized ? 'U.S. Treasury' : (property.listing_source || (sourceKey.charAt(0).toUpperCase() + sourceKey.slice(1)))}
          </Badge>
          {isElite && !property.is_seized && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh data from Zillow"
              className="ml-1 p-1 rounded bg-slate-800/80 hover:bg-slate-700 border-0 text-white disabled:opacity-50"
              data-testid="property-card-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          )}
        </div>
      </div>

      <CardContent className="p-3.5">
        <h3 className="font-semibold text-sm text-foreground mb-0.5 truncate" data-testid="property-address">{property.address}</h3>
        <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
          <MapPin className="w-3 h-3 flex-shrink-0" />
          {property.city}, {property.state} {property.zip_code}
        </p>

        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
          <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" />{property.bedrooms}</span>
          <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{property.bathrooms}</span>
          <span className="flex items-center gap-1"><Square className="w-3.5 h-3.5" />{(property.sqft || 0).toLocaleString()}</span>
          {property.days_on_market > 0 && <span className="flex items-center gap-1 ml-auto"><Clock className="w-3 h-3" />{property.days_on_market}d</span>}
        </div>

        {property.cached_at && (
          <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1 mb-2" data-testid="freshness-indicator">
            <RefreshCw className="w-2.5 h-2.5" /> Updated {getRelativeTime(property.cached_at)}
          </p>
        )}

        {(analysis?.data_sources || property.listing_source) && (
          <div className="mb-2 flex items-center justify-between gap-2 flex-wrap" data-testid="property-card-sources">
            <DataSourcesFooter dataSources={analysis?.data_sources} property={property} />
            {analysis?.data_confidence && (
              <ConfidenceChip confidence={analysis.data_confidence} size="xs" />
            )}
          </div>
        )}

        {analysis ? (
          <>
            {/* Strategy recommendation tag */}
            {bestStrategy && (
              <div className="pt-1.5">
                <Badge className={`text-[9px] px-1.5 py-0 ${bestStrategy === 'flip' ? 'bg-orange-500/10 text-orange-500 border-orange-500/20' : bestStrategy === 'rental' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-muted text-muted-foreground border-border'}`} data-testid="strategy-tag">
                  {bestStrategy === 'flip' ? 'Best as Flip' : bestStrategy === 'rental' ? 'Best as Rental' : 'Not a Deal'}
                </Badge>
              </div>
            )}

            {/* ═══ FLIP MODE CARD ═══ */}
            {isFlipMode && ff ? (
              <div className="pt-2.5 border-t border-orange-500/20" data-testid="flip-mode-metrics">
                {/* Over ARV warning */}
                {overARV && (
                  <div className="flex items-center gap-1.5 p-2 mb-2 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="over-arv-warning">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                    <span className="text-[10px] font-bold text-red-500">Over ARV — not a flip candidate</span>
                  </div>
                )}

                {/* ARV + Rehab + MAO */}
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div>
                    <p className="text-[9px] text-muted-foreground uppercase">ARV</p>
                    <p className="font-mono text-sm font-bold">{formatCurrency(ff.arv)}</p>
                    {ff.arv_low > 0 && ff.arv_high > 0 && (
                      <p className="text-[7px] text-muted-foreground">{formatCurrency(ff.arv_low)}–{formatCurrency(ff.arv_high)}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[9px] text-muted-foreground uppercase">Rehab</p>
                    <p className="font-mono text-sm font-bold text-red-400">{formatCurrency(ff.rehab_total)}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-muted-foreground uppercase">Conf.</p>
                    <p className={`text-sm font-bold ${ff.arv_confidence === 'high' ? 'text-emerald-500' : ff.arv_confidence === 'medium' ? 'text-blue-400' : 'text-amber-500'}`}>
                      {(ff.arv_confidence || 'low').charAt(0).toUpperCase() + (ff.arv_confidence || 'low').slice(1)}
                    </p>
                    <p className="text-[7px] text-muted-foreground">{ff.arv_comp_count || 0} comps</p>
                  </div>
                </div>

                {/* Profit + Margin */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <p className="text-[9px] text-muted-foreground uppercase">Net Profit</p>
                    <p className={`font-mono text-base font-bold ${ff.net_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`} data-testid="flip-profit">{formatCurrency(ff.net_profit)}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-muted-foreground uppercase">Project Margin</p>
                    <p className={`font-mono text-base font-bold ${(ff.project_margin || 0) >= 0.07 ? 'text-emerald-500' : (ff.project_margin || 0) > 0 ? 'text-amber-500' : 'text-red-400'}`}>
                      {ff.net_profit > 0 ? `${((ff.project_margin || 0) * 100).toFixed(1)}%` : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* MAO Rules — compact 70/75/80 */}
                {ff.arv > 0 && (
                  <div className="grid grid-cols-3 gap-1 mb-2" data-testid="flip-mao-rules">
                    {[
                      { pct: 70, mao: ff.mao_70 || Math.round(ff.arv * 0.70 - (ff.rehab_total || 0)) },
                      { pct: 75, mao: ff.mao_75 || Math.round(ff.arv * 0.75 - (ff.rehab_total || 0)) },
                      { pct: 80, mao: ff.mao_80 || Math.round(ff.arv * 0.80 - (ff.rehab_total || 0)) },
                    ].map(({ pct, mao }) => {
                      const passes = ff.purchase_price <= mao;
                      return (
                        <div key={pct} className={`text-center p-1 rounded border ${passes ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                          <p className="text-[8px] text-muted-foreground">{pct}%</p>
                          <p className={`font-mono text-[10px] font-bold ${passes ? 'text-emerald-500' : 'text-red-400'}`}>{formatCurrency(mao)}</p>
                          <p className={`text-[7px] font-semibold ${passes ? 'text-emerald-500' : 'text-red-400'}`}>{passes ? 'PASS' : 'FAIL'}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Viable price hint */}
                {ff.net_profit <= 0 && ff.mao > 0 && (
                  <p className="text-[9px] text-amber-500 italic" data-testid="flip-viable-hint">
                    Becomes viable at {formatCurrency(ff.mao)} or below
                  </p>
                )}

                {/* Flip Score bar */}
                <div className="pt-2 mt-2 border-t border-border/30" data-testid="flip-score-section">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Flip Score</span>
                      {(() => {
                        const v = ff.verdict || '';
                        const isStrong = v.startsWith('Strong');
                        const isPossible = v.startsWith('Possible');
                        const isThin = v.startsWith('Thin');
                        const vColor = isStrong ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/25' : isPossible ? 'bg-blue-500/15 text-blue-400 border-blue-500/25' : isThin ? 'bg-amber-500/15 text-amber-500 border-amber-500/25' : 'bg-red-500/15 text-red-400 border-red-500/25';
                        const vLabel = isStrong ? 'Strong Flip' : isPossible ? 'Possible Flip' : isThin ? 'Thin Deal' : 'Not a Flip';
                        return <Badge className={`text-[8px] px-1.5 py-0 font-semibold ${vColor}`} data-testid="flip-verdict-badge">{vLabel}</Badge>;
                      })()}
                    </div>
                    <span className={`font-mono text-base font-bold ${dealScore >= 60 ? 'text-emerald-500' : dealScore >= 40 ? 'text-amber-500' : 'text-red-500'}`}>
                      {dealScore}<span className="text-[10px] text-muted-foreground font-normal">/100</span>
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${dealScore >= 60 ? 'bg-emerald-500' : dealScore >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${dealScore}%` }} />
                  </div>
                  <p className="text-[8px] text-muted-foreground mt-1.5 text-center cursor-pointer hover:text-orange-500 transition-colors" onClick={(e) => { e.stopPropagation(); onClick?.(); }} data-testid="how-calculated-link">
                    How this deal was calculated &rarr;
                  </p>
                </div>
              </div>
            ) : isFlipMode && !ff ? (
              <div className="pt-2.5 pb-2 border-t border-border/50 text-center">
                <p className="text-xs text-muted-foreground">Flip analysis loading...</p>
              </div>
            ) : (
            <>
            {/* ═══ RENTAL MODE CARD ═══ */}
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-2.5 pb-2 border-t border-border/50" data-testid="primary-metrics">
              <div>
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Est. Rent</p>
                <p className="font-mono text-sm font-bold text-emerald-500" data-testid="metric-rent">
                  {formatCurrency(analysis.estimated_monthly_rent)}<span className="text-[9px] font-normal text-muted-foreground">/mo</span>
                </p>
              </div>
              <div>
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Cash Flow</p>
                <p className={`font-mono text-sm font-bold ${analysis.cash_flow_monthly >= 0 ? 'text-emerald-500' : 'text-red-500'}`} data-testid="metric-cashflow">
                  {formatCurrency(analysis.cash_flow_monthly)}<span className="text-[9px] font-normal text-muted-foreground">/mo</span>
                </p>
              </div>
              <div>
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Mortgage</p>
                <p className="font-mono text-sm font-bold text-red-400" data-testid="metric-mortgage">
                  {formatCurrency(analysis.monthly_mortgage)}<span className="text-[9px] font-normal text-muted-foreground">/mo</span>
                </p>
              </div>
              <div>
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Cap Rate</p>
                <p className={`font-mono text-sm font-bold ${analysis.cap_rate >= 5 ? 'text-emerald-500' : 'text-foreground'}`} data-testid="metric-caprate">
                  {formatPercent(analysis.cap_rate)}
                </p>
              </div>
            </div>

            {/* Deal Score bar */}
            <div className="pt-2 pb-1.5 border-t border-border/30" data-testid="deal-score-section">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] text-muted-foreground uppercase tracking-wider">{scoreLabel}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`font-mono text-base font-bold ${dealScore >= 75 ? 'text-emerald-500' : dealScore >= 50 ? 'text-amber-500' : 'text-red-500'}`} data-testid="deal-score-value">
                    {dealScore}
                  </span>
                  <span className="text-[10px] text-muted-foreground">/100</span>
                  <button
                    onClick={handleWhyClick}
                    className="ml-0.5 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="why-score-btn"
                    aria-label="Why this score?"
                  >
                    <Info className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${dealScore >= 75 ? 'bg-emerald-500' : dealScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: `${dealScore}%` }}
                />
              </div>
              {showWhy && (
                <p className="text-[10px] text-muted-foreground mt-1.5 leading-tight italic" data-testid="why-score-text">
                  {getScoreReason(dealScore, showFlipData && flipDealScore != null)}
                </p>
              )}
            </div>

            {/* Strategy Match — criteria fit against the user's saved strategy */}
            {analysis.strategy_match && (
              <div className="pt-1.5 pb-1" data-testid="strategy-match-section">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Your Strategy Match</span>
                  <div className="flex items-center gap-1.5">
                    <span className={`font-mono text-sm font-bold ${analysis.strategy_match.match_score >= 80 ? 'text-emerald-500' : analysis.strategy_match.match_score >= 55 ? 'text-amber-500' : 'text-red-500'}`} data-testid="strategy-match-value">
                      {analysis.strategy_match.match_score}
                    </span>
                    <span className="text-[10px] text-muted-foreground">/100</span>
                  </div>
                </div>
                <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${analysis.strategy_match.match_score >= 80 ? 'bg-emerald-500' : analysis.strategy_match.match_score >= 55 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${analysis.strategy_match.match_score}%` }}
                  />
                </div>
              </div>
            )}

            {/* Rule badges + profitability — rental mode only */}
            <div className="flex items-center gap-1.5 mt-1" data-testid="rule-badges">
              <Badge className={`text-[10px] px-1.5 py-0 ${onePercentRule ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`} data-testid="one-percent-badge">
                {onePercentRule ? <Check className="w-2.5 h-2.5 mr-0.5" /> : <X className="w-2.5 h-2.5 mr-0.5" />}1%
              </Badge>
              <Badge className={`text-[10px] px-1.5 py-0 ${fiftyPercentRule ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`} data-testid="fifty-percent-badge">
                {fiftyPercentRule ? <Check className="w-2.5 h-2.5 mr-0.5" /> : <X className="w-2.5 h-2.5 mr-0.5" />}50%
              </Badge>
              {analysis.brrrr_score >= 60 && (
                <Badge className="text-[10px] px-1.5 py-0 bg-violet-500/10 text-violet-500 border-violet-500/20" data-testid="brrrr-badge">BRRRR {analysis.brrrr_score}</Badge>
              )}
              {dealScore >= 85 && (
                <Badge className="text-[10px] px-1.5 py-0 bg-amber-500/10 text-amber-600 border-amber-500/20" data-testid="top-deal-badge">
                  <Award className="w-2.5 h-2.5 mr-0.5" />Top 5%
                </Badge>
              )}
              {isIncomeProducing !== undefined && (
                <span className={`ml-auto text-[10px] font-semibold flex items-center gap-0.5 ${isIncomeProducing ? 'text-emerald-500' : 'text-red-400'}`} data-testid="profitability-indicator">
                  <TrendingUp className="w-3 h-3" />
                  {isIncomeProducing ? 'Profitable' : 'Not Profitable'}
                </span>
              )}
            </div>
            </>
            )}

            {/* Airbnb Data — visible when toggle is on */}
            {showAirbnbData && analysis?.airbnb && (
              <div className="mt-2 pt-2 border-t border-cyan-500/20 bg-cyan-500/5 -mx-3.5 px-3.5 pb-2" data-testid="airbnb-data-overlay">
                <div className="flex items-center gap-1 mb-1.5">
                  <HomeIcon className="w-3 h-3 text-cyan-500" />
                  <span className="text-[10px] font-semibold text-cyan-500 uppercase tracking-wider">Airbnb / STR</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-[9px] text-muted-foreground">Nightly</p>
                    <p className="font-mono text-xs font-bold">{formatCurrency(analysis.airbnb.nightly_rate)}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-muted-foreground">Occupancy</p>
                    <p className="font-mono text-xs font-bold">{analysis.airbnb.occupancy_pct}%</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-muted-foreground">Net Income</p>
                    <p className={`font-mono text-xs font-bold ${(analysis.airbnb.net_monthly_income || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(analysis.airbnb.net_monthly_income)}<span className="text-[8px] font-normal">/mo</span></p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[9px] text-muted-foreground">Gross: <span className="font-mono font-semibold text-foreground">{formatCurrency(analysis.airbnb.gross_monthly_income)}/mo</span></span>
                  <Badge className={`text-[9px] px-1 py-0 ${analysis.airbnb.better_option === 'airbnb' ? 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20' : 'bg-secondary/50 text-muted-foreground border-border'}`}>
                    {analysis.airbnb.better_option === 'airbnb' ? 'STR Wins' : 'LTR Wins'}
                  </Badge>
                </div>
              </div>
            )}

            {/* Edit numbers hint */}
            <div className="mt-2 pt-2 border-t border-dashed border-border/40" data-testid="edit-numbers-hint">
              <p className="text-[10px] text-muted-foreground/70 text-center flex items-center justify-center gap-1 hover:text-emerald-500 transition-colors cursor-pointer">
                <Info className="w-3 h-3" />
                <span>Based on averages — <span className="underline underline-offset-2">click to customize numbers</span></span>
              </p>
            </div>
          </>
        ) : !property.is_seized ? (
          <div className="pt-3 border-t border-border/50">
            <div className="grid grid-cols-2 gap-2">
              {["Est. Rent", "Cash Flow", "Mortgage", "Cap Rate"].map(label => (
                <div key={label}>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
                  <div className="skeleton h-4 w-16 rounded" />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground text-center mt-2.5 animate-pulse">Analyzing investment...</p>
          </div>
        ) : null}

        {property.is_seized && (
          <div className="mt-2.5 space-y-1.5 p-2 rounded-lg border border-orange-500/20 bg-orange-500/5" data-testid="seized-info">
            {property.auction_date && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Auction</span>
                <span className="font-semibold text-orange-500">{property.auction_date}</span>
              </div>
            )}
            {property.starting_bid != null && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Starting Bid</span>
                <span className="font-mono font-semibold">{formatCurrency(property.starting_bid)}</span>
              </div>
            )}
            <a href={property.listing_url} target="_blank" rel="noopener noreferrer"
              className="block text-center text-xs font-semibold text-orange-500 hover:text-orange-400 border border-orange-500/30 rounded-md py-1 transition-colors"
              data-testid="view-official-listing"
              onClick={(e) => e.stopPropagation()}>
              View Official Listing
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PropertyCard;
